import type { AddWidgetInput } from '../../externalTools';
import type {
    ConcreteIdea,
    IfThenResearchPlan,
    ScannerPlan,
    ScreenerPlan,
    WatchlistPlan,
} from '@thinkalpha/platform-ws-client/contracts/ideas/index.js';
import { inject, injectable } from 'inversify';
import type { AssistantPanelResultDispatcher } from 'src/features/assistant/services/AssistantPanelResultDispatcher/interface';
import { getWidgetDefinition } from 'src/lib/widget/widgets';
import { getIdeaByIdQuery } from 'src/queries/ideas';
import {
    addWidgetToDashboard,
    createAlphaEyeFromSymbol,
    createChartFromSymbol,
    createEventsWithSymbolFilter,
    createResultsWithStrategy,
    createSearchAlphaWithIdea,
} from 'src/store/actions/container';
import { updateActiveAlertsTab } from 'src/store/actions/widgets/alerts';
import { setScannerIdea } from 'src/store/actions/widgets/scanner';
import { setScreenerIdea } from 'src/store/actions/widgets/screener';
import { setWatchlistIdea } from 'src/store/actions/widgets/watchlist';
import type { ReactBindings } from 'src/types/bindings';

@injectable()
export class AssistantPanelResultDispatcherImpl implements AssistantPanelResultDispatcher {
    #log: ReactBindings['Logger'];
    #store: ReactBindings['Store'];
    #workspaceService: ReactBindings['WorkspaceService'];
    #alertService: ReactBindings['AlertService'];
    #queryClient: ReactBindings['QueryClient'];

    constructor(
        @inject('Store') store: ReactBindings['Store'],
        @inject('WorkspaceService') workspaceService: ReactBindings['WorkspaceService'],
        @inject('AlertService') alertService: ReactBindings['AlertService'],
        @inject('QueryClient') queryClient: ReactBindings['QueryClient'],
        @inject('Logger') logger: ReactBindings['Logger'],
    ) {
        this.#store = store;
        this.#workspaceService = workspaceService;
        this.#alertService = alertService;
        this.#queryClient = queryClient;
        this.#log = logger.getSubLogger({ name: 'feat:assistant:AssistantPanelResultDispatcher' });
    }

    async addWidget(input: AddWidgetInput): Promise<void> {
        switch (input.widgetType) {
            case 'alerts': {
                const alertsTabId = await this.#openWidgetAndWaitForTabId('alerts', () => {
                    this.#store.dispatch(addWidgetToDashboard('alerts'));
                });

                if (input.activeAlertsTab) {
                    this.#store.dispatch(updateActiveAlertsTab(alertsTabId, input.activeAlertsTab));
                }
                this.#invalidateAlertsWidget();
                return;
            }
            case 'tradingview-chart':
                this.#store.dispatch(
                    createChartFromSymbol({
                        channelId: null,
                        defaultSymbol: input.symbol,
                        interval: input.interval,
                    }),
                );
                return;
            case 'alpha-eye':
                this.#store.dispatch(createAlphaEyeFromSymbol({ channelId: null, defaultSymbol: input.symbol }));
                return;
            case 'filings':
                await this.#workspaceService.handleAddWidgetAction({
                    type: 'addWidgetToDashboard',
                    widget: {
                        creationType: 'new-filings',
                        channelId: null,
                        symbolFilter: input.symbolFilter,
                        ...(input.filingTypes?.length ? { filingTypes: input.filingTypes } : {}),
                    },
                });
                return;
            case 'news':
                await this.#workspaceService.handleAddWidgetAction({
                    type: 'addWidgetToDashboard',
                    widget: {
                        creationType: 'new-news',
                        channelId: null,
                        symbolFilter: input.symbolFilter ?? null,
                        ideas: input.ideaId ? [input.ideaId] : [],
                    },
                });
                return;
            case 'events':
                await this.#workspaceService.handleAddWidgetAction(
                    createEventsWithSymbolFilter({
                        channelId: null,
                        symbolFilter: input.symbolFilter ?? null,
                        eventTypes: input.eventTypes,
                    }),
                );
                return;
            case 'results':
                if (input.ideaId) {
                    this.#store.dispatch(
                        createResultsWithStrategy({
                            defaultSymbol: '',
                            channelId: null,
                            strategies: [input.ideaId],
                        }),
                    );
                    return;
                }
                break;
            case 'searchalpha': {
                const idea = await this.#fetchIdea(input.ideaId);
                const planType = idea.plan.type ?? 'if-then';

                if (planType !== 'if-then') {
                    throw new Error(`Cannot open a ${input.widgetType} widget with a ${planType} idea.`);
                }

                this.#store.dispatch(
                    createSearchAlphaWithIdea({
                        channelId: null,
                        idea: idea as ConcreteIdea & { plan: IfThenResearchPlan },
                    }),
                );
                return;
            }
            case 'scanner':
            case 'screener':
            case 'watchlist':
                if (input.ideaId) {
                    await this.#openIdeaWidget(input);
                    return;
                }
                break;
        }

        this.#workspaceService.addWidget(input.widgetType);
    }

    async #openIdeaWidget(input: Extract<AddWidgetInput, { widgetType: 'scanner' | 'screener' | 'watchlist' }>) {
        if (!input.ideaId) {
            this.#workspaceService.addWidget(input.widgetType);
            return;
        }

        const idea = await this.#fetchIdea(input.ideaId);
        const planType = idea.plan.type ?? 'if-then';

        if (input.widgetType === 'scanner') {
            if (planType !== 'scanner') {
                throw new Error(`Cannot open a ${input.widgetType} widget with a ${planType} idea.`);
            }

            const scannerTabId = await this.#openWidgetAndWaitForTabId('scanner', () => {
                this.#store.dispatch(addWidgetToDashboard('scanner'));
            });
            this.#store.dispatch(setScannerIdea(scannerTabId, idea as ConcreteIdea & { plan: ScannerPlan }));
            return;
        }

        if (input.widgetType === 'screener') {
            if (planType !== 'screener') {
                throw new Error(`Cannot open a ${input.widgetType} widget with a ${planType} idea.`);
            }

            const screenerTabId = await this.#openWidgetAndWaitForTabId('screener', () => {
                this.#store.dispatch(addWidgetToDashboard('screener'));
            });
            this.#store.dispatch(setScreenerIdea(screenerTabId, idea as ConcreteIdea & { plan: ScreenerPlan }));
            return;
        }

        if (planType !== 'watchlist') {
            throw new Error(`Cannot open a ${input.widgetType} widget with a ${planType} idea.`);
        }

        const watchlistTabId = await this.#openWidgetAndWaitForTabId('watchlist', () => {
            this.#store.dispatch(addWidgetToDashboard('watchlist'));
        });
        this.#store.dispatch(setWatchlistIdea(watchlistTabId, idea as ConcreteIdea & { plan: WatchlistPlan }, false));
    }

    #getExistingTabIds(widgetType: AddWidgetInput['widgetType']) {
        const existingTabIds = new Set<string>();

        for (const tab of Object.values(this.#store.getState().tab.byId)) {
            if (tab && tab.widget.type === widgetType) {
                existingTabIds.add(tab.id);
            }
        }

        return existingTabIds;
    }

    #getFirstTabIdByWidgetType(widgetType: AddWidgetInput['widgetType']) {
        for (const tab of Object.values(this.#store.getState().tab.byId)) {
            if (tab && tab.widget.type === widgetType) {
                return tab.id;
            }
        }

        return null;
    }

    async #waitForNewTabId(widgetType: AddWidgetInput['widgetType'], existingTabIds: ReadonlySet<string>) {
        const getNewTabId = () => {
            for (const tab of Object.values(this.#store.getState().tab.byId)) {
                if (tab && tab.widget.type === widgetType && !existingTabIds.has(tab.id)) {
                    return tab.id;
                }
            }

            return null;
        };

        const currentTabId = getNewTabId();
        if (currentTabId) {
            return currentTabId;
        }

        return await new Promise<string>((resolve, reject) => {
            const unsubscribe = this.#store.subscribe(() => {
                const newTabId = getNewTabId();
                if (!newTabId) {
                    return;
                }

                clearTimeout(timeoutId);
                unsubscribe();
                resolve(newTabId);
            });

            const timeoutId = setTimeout(() => {
                unsubscribe();
                reject(new Error(`Timed out waiting for ${widgetType} widget creation`));
            }, 15_000);
        });
    }

    async #openWidgetAndWaitForTabId(widgetType: AddWidgetInput['widgetType'], openWidget: () => void) {
        const existingTabId = this.#getFirstTabIdByWidgetType(widgetType);
        const existingTabIds = this.#getExistingTabIds(widgetType);
        const maxInstances = getWidgetDefinition(widgetType).maxInstances;

        if (existingTabId && maxInstances !== undefined && existingTabIds.size >= maxInstances) {
            openWidget();
            return existingTabId;
        }

        openWidget();

        try {
            return await this.#waitForNewTabId(widgetType, existingTabIds);
        } catch (err) {
            const currentTabId = this.#getFirstTabIdByWidgetType(widgetType);
            if (existingTabId && currentTabId === existingTabId) {
                return existingTabId;
            }

            throw err;
        }
    }

    async #fetchIdea(ideaId: string): Promise<ConcreteIdea> {
        const idea = await Promise.race([
            this.#queryClient.fetchQuery(getIdeaByIdQuery(ideaId)),
            new Promise<never>((_, reject) =>
                setTimeout(() => reject(new Error('Idea fetch timed out after 15s')), 15_000),
            ),
        ]);
        if (!idea) {
            throw new Error(`Idea ${ideaId} not found`);
        }
        return idea;
    }

    #invalidateAlertsWidget() {
        void this.#alertService.invalidateAlerts().catch((err) => {
            // Non-critical; the alert will appear on the next natural refetch.
            if (TRACE) {
                this.#log.trace('Failed to invalidate alerts after widget creation', {
                    error: err instanceof Error ? err.message : err,
                });
            }
        });
    }
}
